import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

// Cloudflare Turnstile widget for the contact form. The script is loaded once on demand
// (explicit render) instead of in public/index.html, so pages without a form never call
// Cloudflare. A token is single-use: the parent calls reset() after every submit.
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise = null;

function loadTurnstileScript() {
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve(window.turnstile);
            script.onerror = () => {
                scriptPromise = null;
                reject(new Error("Turnstile script could not be loaded"));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ siteKey, language, onTokenChange }, ref) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onTokenChangeRef = useRef(onTokenChange);
    onTokenChangeRef.current = onTokenChange;

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
            }
            onTokenChangeRef.current(null);
        }
    }), []);

    useEffect(() => {
        let cancelled = false;
        loadTurnstileScript()
            .then((turnstile) => {
                if (cancelled || !containerRef.current) {
                    return;
                }
                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    language: language || "auto",
                    theme: "light",
                    callback: (token) => onTokenChangeRef.current(token),
                    "expired-callback": () => onTokenChangeRef.current(null),
                    "error-callback": () => onTokenChangeRef.current(null),
                });
            })
            .catch((err) => console.error(err));
        return () => {
            cancelled = true;
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [siteKey, language]);

    return <div ref={containerRef} />;
});

export default Turnstile;
